/**
 * Injectable CatalogClient wrapper for STAC catalog and dataset operations.
 * Handles real backend endpoints with proper error states per WP-E3 brief.
 * 
 * Each method calls real backend endpoints using fetch; on network/404/501
 * returns a typed error state, not fake success data.
 */

export interface CatalogError {
  ok: false;
  reason: 'backend-not-deployed' | 'network-error' | 'not-found' | 'server-error' | 'unauthorized';
  message?: string;
}

export interface CatalogSuccess<T> {
  ok: true;
  data: T;
}

export type CatalogResult<T> = CatalogSuccess<T> | CatalogError;

export interface Layer {
  id: string;
  name: string;
  description?: string;
  type: 'raster' | 'vector' | 'pointcloud' | '3dtiles' | 'dem';
  format: string;
  bbox?: [number, number, number, number];
  crs?: string;
  temporal?: boolean;
}

export interface Dataset {
  id: string;
  collection_id: string;
  name: string;
  description?: string;
  geometry?: any; // GeoJSON geometry
  bbox?: [number, number, number, number];
  properties?: Record<string, any>;
  assets?: Record<string, any>;
  links?: any[];
}

export interface UploadJob {
  id: string;
  status: 'waiting' | 'validating' | 'ready' | 'running' | 'needs-input' | 'failed' | 'completed' | 'cancelled';
  fileName: string;
  fileSize: number;
  progress?: number;
  stage?: string;
  error?: string;
}

export interface LineageNode {
  id: string;
  type: 'upload' | 'validate' | 'conversion' | 'tile' | 'render';
  status: 'pending' | 'running' | 'completed' | 'failed';
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface Lineage {
  nodes: LineageNode[];
  edges: Array<{ from: string; to: string }>;
}

export class CatalogClient {
  private baseUrl: string;

  constructor(baseUrl = '/api/v1') {
    this.baseUrl = baseUrl;
  }

  private async makeRequest<T>(endpoint: string, options?: RequestInit): Promise<CatalogResult<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      });

      if (response.status === 404) {
        return { ok: false, reason: 'not-found', message: 'Resource not found' };
      }

      if (response.status === 401 || response.status === 403) {
        return { ok: false, reason: 'unauthorized', message: 'Access denied' };
      }

      if (response.status === 501) {
        return { ok: false, reason: 'backend-not-deployed', message: 'Backend service not deployed' };
      }

      if (!response.ok) {
        return { ok: false, reason: 'server-error', message: `Server error: ${response.status}` };
      }

      const data = await response.json();
      return { ok: true, data };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return { ok: false, reason: 'backend-not-deployed', message: 'Backend not reachable' };
      }
      return { ok: false, reason: 'network-error', message: 'Network request failed' };
    }
  }

  /**
   * List available layers from catalog (BE-001/BE-004)
   */
  async listLayers(): Promise<CatalogResult<Layer[]>> {
    return this.makeRequest<Layer[]>('/catalog/layers');
  }

  /**
   * Get dataset detail by ID (BE-005)
   */
  async getDataset(id: string): Promise<CatalogResult<Dataset>> {
    return this.makeRequest<Dataset>(`/catalog/items/${id}`);
  }

  /**
   * Create upload via TUS (BE-040/BE-125) - returns upload URL or backend-not-deployed state
   */
  async createUpload(file: File, metadata?: Record<string, string>): Promise<CatalogResult<{ uploadUrl: string; jobId: string }>> {
    const uploadMetadata = {
      filename: file.name,
      filetype: file.type,
      size: file.size.toString(),
      ...metadata,
    };

    return this.makeRequest<{ uploadUrl: string; jobId: string }>('/uploads', {
      method: 'POST',
      body: JSON.stringify({
        metadata: uploadMetadata,
      }),
    });
  }

  /**
   * Get job status (BE-041)
   */
  async getJob(id: string): Promise<CatalogResult<UploadJob>> {
    return this.makeRequest<UploadJob>(`/jobs/${id}`);
  }

  /**
   * Get lineage/provenance for a dataset (BE-007)
   */
  async getLineage(id: string): Promise<CatalogResult<Lineage>> {
    return this.makeRequest<Lineage>(`/catalog/lineage/${id}`);
  }

  /**
   * Search catalog (BE-003)
   */
  async searchCatalog(params: {
    bbox?: [number, number, number, number];
    datetime?: string;
    collections?: string[];
    limit?: number;
    query?: Record<string, any>;
  }): Promise<CatalogResult<{ items: Dataset[]; total: number }>> {
    return this.makeRequest<{ items: Dataset[]; total: number }>('/catalog/search', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }
}

// Default client instance
export const catalogClient = new CatalogClient();